/**
 * Dev-mode AuthClient — used when VITE_AUTH_BASE_URL points at local-stack
 * (any `http://localhost*` issuer). Bypasses OIDC entirely; reads the
 * `cp_dev_user` cookie set by local-stack's /dev-auth/login page and
 * synthesizes a Session + a base64url JWT-shaped access token whose payload
 * encodes the seeded user identity.
 *
 * Local-stack accepts the token by reading the `sub` claim from the payload
 * (no signature verification — see `services/api/src/handler.ts` dev-mode
 * fast path). Production never enters this code path because real builds set
 * `VITE_AUTH_BASE_URL=https://auth.molaris.ai`.
 *
 * This entire module is throwaway: it will be deleted when the Clerk
 * migration lands. Keep it small + obvious so the diff at deletion is clean.
 */
import type {
  AuthClient,
  SignInOptions,
} from "./client.js";
import type { AuthConfig } from "./config.js";
import type { SignOutResult } from "./sign-out.js";
import type {
  AuthError,
  AuthErrorListener,
  Session,
  SessionListener,
  Tenant,
} from "./types.js";

interface DevWhoamiResponse {
  userId: string;
  email: string;
  fullName: string | null;
  tenantId: string;
  userType: string;
  portalRole: Session["portalRole"];
}

function base64UrlEncode(value: string): string {
  if (typeof window === "undefined") {
    return Buffer.from(value, "utf8")
      .toString("base64")
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
  }
  return btoa(value).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function synthAccessToken(who: DevWhoamiResponse): string {
  const now = Math.floor(Date.now() / 1000);
  const header = base64UrlEncode(JSON.stringify({ alg: "none", typ: "JWT" }));
  const payload = base64UrlEncode(
    JSON.stringify({
      sub: who.userId,
      email: who.email,
      iat: now,
      exp: now + 60 * 60, // 1h synthetic expiry
      session_id: `dev-${who.userId}`,
      molaris: {
        user_global_id: who.userId,
        user_local_id: who.userId,
        user_tenant_id: who.tenantId,
        email: who.email,
        full_name: who.fullName,
        internal_role_ids: [],
        org_unit_scope_ids: [],
        product_entitlements: ["clearpath"],
        mfa_verified: true,
        mfa_verified_at: new Date().toISOString(),
        portal_role: who.portalRole,
        is_jit_grant: false,
        jit_grant_id: null,
      },
    }),
  );
  return `${header}.${payload}.dev`;
}

function sessionFromWhoami(who: DevWhoamiResponse): Session {
  const now = Math.floor(Date.now() / 1000);
  return {
    userGlobalId: who.userId,
    userLocalId: who.userId,
    userTenantId: who.tenantId,
    email: who.email,
    fullName: who.fullName,
    internalRoleIds: [],
    orgUnitScopeIds: [],
    productEntitlements: ["clearpath"],
    mfaVerified: true,
    mfaVerifiedAt: new Date().toISOString(),
    portalRole: who.portalRole,
    isJitGrant: false,
    jitGrantId: null,
    sessionId: `dev-${who.userId}`,
    issuedAt: now,
    expiresAt: now + 60 * 60,
  };
}

/**
 * ENG-3924 — the local-stack picker (http://localhost:<port>/dev-auth) is the
 * way to switch person locally, kept open in its own tab. Two halves make it
 * work like an in-app switcher:
 *   * The page you were on is remembered in a host-wide cookie when you leave
 *     the tab, keyed by the app's port (one per app; the picker mount reads its
 *     own app's). Picking someone returns you there instead of the app's home.
 *     Local-stack dev-auth server.ts reads it — the two names must match.
 *   * When an app tab is shown again and the picker now holds a different
 *     person, the tab reloads on the same page as them.
 */
export const DEV_RETURN_TO_COOKIE_PREFIX = "cp_dev_return_to_";

function followLocalPicker(fetchWhoami: () => Promise<DevWhoamiResponse | null>, signedInAs: () => string | null): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  const remember = (): void => {
    document.cookie =
      `${DEV_RETURN_TO_COOKIE_PREFIX}${window.location.port || "80"}=${encodeURIComponent(window.location.href)}` +
      "; Path=/; Max-Age=86400; SameSite=Lax";
  };
  const follow = async (): Promise<void> => {
    const current = signedInAs();
    if (document.visibilityState !== "visible" || current === null) return;
    const who = await fetchWhoami();
    // Only a DIFFERENT person reloads. No answer (signed out in the picker, or
    // the backend restarting) leaves the tab as it is.
    if (who && who.userId !== current) window.location.reload();
  };
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") remember();
    else void follow();
  });
  window.addEventListener("pagehide", remember);
  window.addEventListener("focus", () => void follow());
}

export function createDevAuthClient(config: AuthConfig): AuthClient {
  // authBaseUrl is the dev-auth router root, e.g. http://localhost:4000/dev-auth
  const authBase = config.authBaseUrl.replace(/\/$/, "");
  const sessionListeners = new Set<SessionListener>();
  const errorListeners = new Set<AuthErrorListener>();
  let currentSession: Session | null = null;
  let currentToken: string | null = null;
  let coldLoadInflight: Promise<Session | null> | null = null;

  function emitSession(): void {
    for (const l of sessionListeners) l(currentSession);
  }

  async function fetchWhoami(): Promise<DevWhoamiResponse | null> {
    try {
      const res = await fetch(`${authBase}/whoami`, {
        credentials: "include",
      });
      if (!res.ok) return null;
      const body = (await res.json()) as { identity: DevWhoamiResponse | null };
      return body.identity ?? null;
    } catch {
      return null;
    }
  }

  followLocalPicker(fetchWhoami, () => currentSession?.userLocalId ?? null);

  async function getCurrentUser(): Promise<Session | null> {
    if (currentSession) return currentSession;
    if (coldLoadInflight) return coldLoadInflight;
    coldLoadInflight = (async () => {
      const who = await fetchWhoami();
      if (!who) {
        currentSession = null;
        currentToken = null;
        emitSession();
        return null;
      }
      currentSession = sessionFromWhoami(who);
      currentToken = synthAccessToken(who);
      emitSession();
      return currentSession;
    })().finally(() => {
      coldLoadInflight = null;
    });
    return coldLoadInflight;
  }

  async function signIn(opts: SignInOptions = {}): Promise<void> {
    if (typeof window === "undefined") return;
    const returnTo = opts.returnTo ?? window.location.href;
    const url = new URL(`${authBase}/login`);
    url.searchParams.set("redirect", returnTo);
    window.location.href = url.toString();
  }

  async function signOut(): Promise<SignOutResult> {
    // Best-effort local-stack logout. Deliberately NOT routed through
    // classifySignOutResponse: dev-auth's GET /logout 302-redirects to /login
    // (local-stack/src/dev-auth/server.ts), so a followed redirect resolves
    // with `redirected === true` — which the CONTRACT v2 classifier rejects as
    // `unexpected` (it accepts ONLY a bare, non-redirected 200) — and a
    // cross-origin miss rejects to `network`. Either would flip the verdict off
    // `signed_out` and, once gated, silently strip the navigation this dev
    // shell has always performed. There is no real session or probe here, so
    // the honest result is a session-less `signed_out`, mirroring dev-client.
    try {
      await fetch(`${authBase}/logout`, {
        method: "GET",
        credentials: "include",
      });
    } catch {
      // best-effort
    }
    currentSession = null;
    currentToken = null;
    emitSession();
    const result: SignOutResult = { verdict: "signed_out", probe: "not_run" };
    // Navigate UNCONDITIONALLY, matching the real client (client.ts
    // navigateToPostLogout): the verdict never gates navigation in T3.
    if (typeof window !== "undefined") {
      window.location.href = config.postLogoutUri;
    }
    return result;
  }

  return {
    config,
    signIn,
    signOut,
    handleCallback: async (): Promise<Session> => {
      const s = await getCurrentUser();
      if (!s) throw new Error("Dev auth: no session after callback");
      return s;
    },
    getAccessToken: async (): Promise<string | null> => {
      if (currentToken) return currentToken;
      await getCurrentUser();
      return currentToken;
    },
    getCurrentUser,
    switchTenant: async (): Promise<Session> => {
      const s = await getCurrentUser();
      if (!s) throw new Error("Dev auth: no session");
      return s;
    },
    refreshSession: async (): Promise<Session> => {
      // Local dev-auth has no token re-mint; the session is already current.
      const s = await getCurrentUser();
      if (!s) throw new Error("Dev auth: no session");
      return s;
    },
    listTenantMemberships: async (): Promise<Tenant[]> => {
      const s = await getCurrentUser();
      if (!s) return [];
      return [{ id: s.userTenantId, name: "Local Dev Tenant", slug: "local-dev" }];
    },
    onSessionChange: (listener: SessionListener): (() => void) => {
      sessionListeners.add(listener);
      // Emit current state immediately so consumers don't wait.
      listener(currentSession);
      return () => sessionListeners.delete(listener);
    },
    onAuthError: (listener: AuthErrorListener): (() => void) => {
      errorListeners.add(listener);
      return () => errorListeners.delete(listener);
    },
    emitAuthError: (error?: AuthError): void => {
      currentSession = null;
      currentToken = null;
      for (const l of errorListeners) l(error);
      emitSession();
    },
  };
}

export function isDevAuthBaseUrl(authBaseUrl: string): boolean {
  return /^http:\/\/localhost(:\d+)?(\/|$)/.test(authBaseUrl);
}
